import { useEffect, useState } from "react";
import {
  Briefcase,
  Users,
  CheckCircle2,
  FileText,
  Video,
  Sparkles,
  BellRing,
} from "lucide-react";

/* -----------------------------------------------------------
   Hireloop PRO — animated platform showreel.
   Auto-cycles through 4 product "screens" every 4 seconds.
   Pure CSS / React; no video files, no extra deps.
----------------------------------------------------------- */

const SCREENS = ["dashboard", "resumes", "interview", "live"];

export default function PlatformShowreel() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SCREENS.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="platform-showreel"
      className="relative aspect-[4/5] overflow-hidden border border-slate-200 bg-slate-900"
    >
      {/* "Browser chrome" */}
      <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-800 px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <div className="ml-3 flex-1 truncate rounded-sm bg-slate-700 px-2 py-1 text-[10px] font-mono text-slate-300">
          hireloop.pro/{SCREENS[idx]}
        </div>
      </div>

      <div className="relative h-[calc(100%-2.25rem)] bg-white">
        <ScreenDashboard active={idx === 0} />
        <ScreenResumes active={idx === 1} />
        <ScreenInterview active={idx === 2} />
        <ScreenLive active={idx === 3} />
      </div>

      {/* Progress dots */}
      <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
        {SCREENS.map((s, i) => (
          <button
            key={s}
            onClick={() => setIdx(i)}
            data-testid={`showreel-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50"
            }`}
            aria-label={`Show screen ${s}`}
          />
        ))}
      </div>

      {/* Active screen label */}
      <div className="absolute left-3 top-12 z-20 border border-white/10 bg-black/60 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200 backdrop-blur-sm">
        {SCREENS[idx]}
      </div>

      <style>{`
        @keyframes slideIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes bobIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* -------------------- individual screens -------------------- */

function ScreenShell({ active, children }) {
  return (
    <div
      className={`absolute inset-0 transition-opacity duration-700 ${
        active ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      {children}
    </div>
  );
}

function ScreenDashboard({ active }) {
  return (
    <ScreenShell active={active}>
      <div className="space-y-3 p-5 text-slate-900">
        <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Dashboard
        </div>
        <div className="text-base font-bold leading-tight">
          Good morning, Hiring Manager.
        </div>
        <div className="mt-2 grid grid-cols-3 gap-px bg-slate-200">
          {[
            { label: "Open jobs", v: "4", bg: "bg-emerald-600" },
            { label: "Candidates", v: "27", bg: "bg-blue-700" },
            { label: "Completed", v: "13", bg: "bg-slate-900" },
          ].map((k) => (
            <div key={k.label} className={`${k.bg} p-3 text-white`}>
              <div className="text-[7px] font-bold uppercase tracking-[0.2em] opacity-80">
                {k.label}
              </div>
              <div className="mt-1 text-xl font-bold">{k.v}</div>
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          {[
            { title: "Senior Backend Engineer", c: 12, icon: Briefcase },
            { title: "Frontend Developer", c: 9, icon: Briefcase },
            { title: "Product Designer", c: 6, icon: Briefcase },
          ].map((j, i) => (
            <div
              key={i}
              className="flex items-center justify-between border border-slate-900 p-2.5"
              style={{
                animation: active
                  ? `slideIn 0.6s ease-out ${i * 0.15}s both`
                  : "none",
              }}
            >
              <div className="flex items-center gap-2">
                <j.icon className="h-3 w-3 text-blue-700" />
                <span className="text-xs font-medium">{j.title}</span>
              </div>
              <span className="text-[10px] font-bold text-red-600">
                {j.c} candidates
              </span>
            </div>
          ))}
        </div>
      </div>
    </ScreenShell>
  );
}

function ScreenResumes({ active }) {
  const rows = [
    { n: "Jordan A.", score: 92, badge: "emerald" },
    { n: "Priya R.", score: 88, badge: "emerald" },
    { n: "Marcus T.", score: 71, badge: "blue" },
    { n: "Lin S.", score: 54, badge: "amber" },
    { n: "Felipe O.", score: 38, badge: "red" },
  ];
  return (
    <ScreenShell active={active}>
      <div className="space-y-2 p-5 text-slate-900">
        <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Resume ranking
        </div>
        <div className="text-base font-bold leading-tight">
          AI scored 5 résumés in 4.2s.
        </div>
        <div className="mt-2 border border-slate-200">
          {rows.map((r, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 border-b border-slate-100 p-2.5 last:border-0 ${
                r.score >= rows[Math.min(4, rows.length - 1)].score
                  ? ""
                  : ""
              } ${i < 5 && r.score >= 71 ? "bg-emerald-50/60" : ""}`}
              style={{
                animation: active
                  ? `slideIn 0.5s ease-out ${i * 0.12}s both`
                  : "none",
              }}
            >
              <span className="flex h-6 w-6 items-center justify-center bg-slate-900 text-[10px] font-bold text-white">
                {i + 1}
              </span>
              <div className="flex-1 truncate text-xs font-medium">{r.n}</div>
              <span
                className={`border px-1.5 py-0.5 text-[9px] font-bold ${
                  r.badge === "emerald"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : r.badge === "blue"
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : r.badge === "amber"
                    ? "border-amber-200 bg-amber-50 text-amber-700"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                {r.score}/100
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-bold text-blue-700">
          <FileText className="h-3 w-3" /> Invite top 5 to interview
        </div>
      </div>
    </ScreenShell>
  );
}

function ScreenInterview({ active }) {
  return (
    <ScreenShell active={active}>
      <div className="relative h-full bg-slate-900 text-white">
        <div className="space-y-3 p-5">
          <div className="flex items-center justify-between text-[9px] font-bold uppercase tracking-[0.22em]">
            <span className="text-blue-300 inline-flex items-center gap-1">
              <Video className="h-3 w-3" /> Senior Backend Eng.
            </span>
            <span className="text-slate-400">Q 2 of 5</span>
          </div>
          <div className="h-1 w-full bg-white/10">
            <div
              className="h-full bg-blue-400"
              style={{ width: active ? "42%" : "20%", transition: "width 3s ease-out" }}
            />
          </div>
          <div className="border border-white/10 bg-white/5 p-3">
            <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-blue-300">
              Question
            </div>
            <p className="mt-1 text-sm font-medium leading-snug">
              Walk me through a system you designed end-to-end. What trade-offs did you make?
            </p>
          </div>
          <div className="relative aspect-video overflow-hidden border border-white/10 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900">
            <div className="absolute left-2 top-2 inline-flex items-center gap-1.5 border border-red-500 bg-black/60 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.15em] text-red-400">
              <span
                className="h-1.5 w-1.5 rounded-full bg-red-500"
                style={{
                  animation: active ? "pulseDot 1s ease-in-out infinite" : "none",
                }}
              />
              Rec · 00:47
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-14 w-14 rounded-full bg-white/10 backdrop-blur-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10">
            {[
              { l: "Quality", v: "8.6" },
              { l: "Pace", v: "142" },
              { l: "Fillers", v: "3" },
            ].map((k) => (
              <div key={k.l} className="bg-slate-900/40 p-2">
                <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-slate-400">
                  {k.l}
                </div>
                <div className="mt-1 text-base font-bold text-blue-300">{k.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}

function ScreenLive({ active }) {
  return (
    <ScreenShell active={active}>
      <div className="relative h-full bg-slate-950 text-white">
        <div className="grid h-full grid-cols-2 gap-1 p-3">
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-700">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-white/15" />
            </div>
            <div className="absolute bottom-2 left-2 border border-white/20 bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em]">
              You
            </div>
          </div>
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-800 to-slate-800">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-white/15" />
            </div>
            <div className="absolute bottom-2 left-2 border border-emerald-300/40 bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-300">
              ● Jordan A.
            </div>
          </div>
        </div>
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">
          {[Video, Sparkles, CheckCircle2, BellRing].map((Icon, i) => (
            <div
              key={i}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm"
              style={{
                animation: active
                  ? `bobIn 0.5s ease-out ${i * 0.1}s both`
                  : "none",
              }}
            >
              <Icon className="h-3.5 w-3.5 text-white" />
            </div>
          ))}
        </div>
        <div className="absolute right-3 top-3 inline-flex items-center gap-1 border border-emerald-300/40 bg-black/60 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-300">
          <Users className="h-3 w-3" /> Live · 1:1
        </div>
      </div>
    </ScreenShell>
  );
}
